import { useMemo, type CSSProperties } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faMedal, faReceipt, faSackDollar, faUser } from '@fortawesome/free-solid-svg-icons';

import 'media/css/BangXepHangNhanvienTheoNgay2.scss';
import __helpers from 'helpers/index';
import { useGetTodayStaffRank } from 'queries/analytics.query';

interface StaffRankItem {
  user_id: number | string;
  display_name: string;
  user_avatar?: string | null;
  totalOrders: number;
  totalRevenue: number;
}

interface RankingViewItem extends StaffRankItem {
  rank: number | null;
  isQualified: boolean;
  isPlaceholder?: boolean;
}

const EMPTY_PLACEHOLDERS: RankingViewItem[] = [
  {
    user_id: 'placeholder-first',
    display_name: 'Chưa có ai',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 1,
    isQualified: true,
    isPlaceholder: true,
  },
  {
    user_id: 'placeholder-second',
    display_name: 'Chưa có ai',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 2,
    isQualified: true,
    isPlaceholder: true,
  },
  {
    user_id: 'placeholder-third',
    display_name: 'Chưa có ai',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 3,
    isQualified: true,
    isPlaceholder: true,
  },
  {
    user_id: 'placeholder-normal',
    display_name: 'Nhân viên trong danh sách',
    totalOrders: 0,
    totalRevenue: 0,
    rank: null,
    isQualified: false,
    isPlaceholder: true,
  },
];

const currencyFormatter = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat('vi-VN');

const MINIMUM_ORDERS_TO_RANK = 10;

const createPodiumPlaceholder = (rank: 1 | 2 | 3): RankingViewItem => ({
  user_id: `placeholder-${rank}`,
  display_name: 'Chưa có ai',
  totalOrders: 0,
  totalRevenue: 0,
  rank,
  isQualified: true,
  isPlaceholder: true,
});

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return 'NV';
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

function getRankText(rank: number | null): string {
  if (rank === null) {
    return 'Chưa xếp hạng';
  }

  return `Hạng ${rank}`;
}

function getCardClassName(item: RankingViewItem): string {
  const classes = ['daily-ranking__card'];

  if (item.rank && item.rank <= 3) {
    classes.push('daily-ranking__card--featured');
    classes.push(`daily-ranking__card--rank-${item.rank}`);
  } else {
    classes.push('daily-ranking__card--normal');
  }

  if (!item.isQualified) {
    classes.push('daily-ranking__card--unqualified');
  }

  if (item.isPlaceholder) {
    classes.push('daily-ranking__card--placeholder');
  }

  return classes.join(' ');
}

export default function BangXepHangNhanVienTheoNgay() {
  const { data: todayStaffRank = [], isLoading } = useGetTodayStaffRank();

  const rankingItems = useMemo<RankingViewItem[]>(() => {
    if (todayStaffRank.length === 0) {
      return EMPTY_PLACEHOLDERS;
    }

    const normalizedData = todayStaffRank.map((staff) => ({
      ...staff,
      totalOrders: Number(staff.totalOrders) || 0,
      totalRevenue: Number(staff.totalRevenue) || 0,
    }));

    const qualifiedStaff = normalizedData
      .filter((staff) => staff.totalOrders >= MINIMUM_ORDERS_TO_RANK)
      .sort((first, second) => {
        if (second.totalRevenue !== first.totalRevenue) {
          return second.totalRevenue - first.totalRevenue;
        }

        if (second.totalOrders !== first.totalOrders) {
          return second.totalOrders - first.totalOrders;
        }

        return first.display_name.localeCompare(second.display_name, 'vi');
      });

    const unqualifiedStaff = normalizedData
      .filter((staff) => staff.totalOrders < MINIMUM_ORDERS_TO_RANK)
      .sort((first, second) => {
        if (second.totalOrders !== first.totalOrders) {
          return second.totalOrders - first.totalOrders;
        }

        if (second.totalRevenue !== first.totalRevenue) {
          return second.totalRevenue - first.totalRevenue;
        }

        return first.display_name.localeCompare(second.display_name, 'vi');
      });

    const rankedItems = qualifiedStaff.map<RankingViewItem>((staff, index) => ({
      ...staff,
      rank: index + 1,
      isQualified: true,
    }));

    const podiumItems = rankedItems.slice(0, 3);

    while (podiumItems.length < 3) {
      podiumItems.push(createPodiumPlaceholder((podiumItems.length + 1) as 1 | 2 | 3));
    }

    return [
      ...podiumItems,
      ...rankedItems.slice(3),
      ...unqualifiedStaff.map<RankingViewItem>((staff) => ({
        ...staff,
        rank: null,
        isQualified: false,
      })),
    ];
  }, [todayStaffRank]);

  const totalQualified = rankingItems.filter((item) => item.isQualified && !item.isPlaceholder).length;

  return (
    <section className="daily-ranking">
      <header className="daily-ranking__header">
        <div>
          <h2 className="daily-ranking__title">Bảng xếp hạng hôm nay</h2>
        </div>

        <div className="daily-ranking__header-meta">
          {isLoading ? (
            <div className="daily-ranking__qualified-count">Đang tổng hợp bảng xếp hạng...</div>
          ) : (
            todayStaffRank.length > 0 && (
              <div className="daily-ranking__qualified-count">{numberFormatter.format(totalQualified)} nhân viên đủ điều kiện</div>
            )
          )}
        </div>
      </header>

      <div className="daily-ranking__scroll">
        <div className="daily-ranking__rail">
          {rankingItems.map((item) => {
            const isTopThree = item.rank !== null && item.rank <= 3;
            const remainingOrders = Math.max(MINIMUM_ORDERS_TO_RANK - item.totalOrders, 0);
            const progressPercentage = Math.min(Math.max((item.totalOrders / MINIMUM_ORDERS_TO_RANK) * 100, 0), 100);
            const progressStyle = { '--daily-ranking-progress': `${progressPercentage}%` } as CSSProperties;
            const avatarSource = item.user_avatar ? __helpers.getMediaLink(item.user_avatar) : undefined;

            return (
              <article key={item.user_id} className={getCardClassName(item)}>
                <div className="daily-ranking__card-top">
                  <div className="daily-ranking__rank">
                    {isTopThree ? (
                      <>
                        <FontAwesomeIcon icon={item.rank === 1 ? faCrown : faMedal} />

                        <span>{getRankText(item.rank)}</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faUser} />
                        <span>{getRankText(item.rank)}</span>
                      </>
                    )}
                  </div>

                  {/* {!item.isQualified && !item.isPlaceholder && <div className="daily-ranking__requirement">Cần {MINIMUM_ORDERS_TO_RANK} đơn</div>} */}
                </div>

                <div className="daily-ranking__identity">
                  <div className="daily-ranking__avatar">
                    {avatarSource && !item.isPlaceholder ? (
                      <img src={avatarSource} alt={item.display_name} loading="lazy" />
                    ) : (
                      <span>{item.isPlaceholder ? '' : getInitials(item.display_name)}</span>
                    )}
                  </div>

                  <div className="daily-ranking__staff-info">
                    <div className="daily-ranking__staff-name">{item.display_name}</div>

                    <div className="daily-ranking__staff-status">
                      {item.isPlaceholder ? 'Chưa có thành tích' : item.isQualified ? 'Đã có số má' : `Còn ${remainingOrders} đơn để xếp hạng`}
                    </div>
                  </div>
                </div>

                <div className="daily-ranking__divider" />

                <div className="daily-ranking__metrics">
                  <div className="daily-ranking__metric">
                    <div className="daily-ranking__metric-icon">
                      <FontAwesomeIcon icon={faReceipt} />
                    </div>

                    <div>
                      <div className="daily-ranking__metric-label">Tổng đơn</div>

                      <div className="daily-ranking__metric-value">{item.isPlaceholder ? '—' : numberFormatter.format(item.totalOrders)}</div>
                    </div>
                  </div>

                  <div className="daily-ranking__metric daily-ranking__metric--revenue">
                    <div className="daily-ranking__metric-icon">
                      <FontAwesomeIcon icon={faSackDollar} />
                    </div>

                    <div>
                      <div className="daily-ranking__metric-label">Doanh thu</div>

                      <div className="daily-ranking__metric-value">{item.isPlaceholder ? '—' : currencyFormatter.format(item.totalRevenue)}</div>
                    </div>
                  </div>
                </div>

                {!item.isQualified && !item.isPlaceholder && (
                  <div className="daily-ranking__progress" style={progressStyle}>
                    <div className="daily-ranking__progress-track">
                      <div className="daily-ranking__progress-value" />
                    </div>
                    <span>
                      {numberFormatter.format(item.totalOrders)}/{numberFormatter.format(MINIMUM_ORDERS_TO_RANK)} đơn
                    </span>
                  </div>
                )}

                {item.rank === 1 && <div className="daily-ranking__winner-glow" />}
              </article>
            );
          })}
        </div>
      </div>

      <footer className="daily-ranking__footer">
        <span className="daily-ranking__footer-dot" />
        Nhân viên dưới {MINIMUM_ORDERS_TO_RANK} đơn vẫn xuất hiện nhưng không được tính thứ hạng.
      </footer>
    </section>
  );
}

